import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { waLink } from '@tiffinify/core';
import type { MessageDto, NeedsYouDto } from '@tiffinify/contracts';
import { TiffinMini, PageLoader, Skel, LoadError } from '../components/ui';
import { Av, Back, Badge as PBadge, Bar, Btn, Btn2, Card as PCard, Chip as PChip, Chips as PChips, Empty as PEmpty, G2, Group, Kick, Note, Row, Screen, Sm, Title, Top } from '../components/p';
import { get } from '../lib/api';
import { endLabel, formatInr, mealsLabel } from '../lib/format';
import { WhatsAppText } from '../lib/whatsapp';
import { useBrief, useMessage, useToday } from '../lib/queries';

const TYPES = [
  { id: 'renewal', label: 'Renewal' },
  { id: 'payment', label: 'Payment due' },
  { id: 'status', label: 'Status' },
] as const;
type Type = (typeof TYPES)[number]['id'];
const typeFrom = (v: string | null): Type => TYPES.find((t) => t.id === v)?.id ?? 'renewal';
const typeFor = (n: Pick<NeedsYouDto, 'reasons'>): Type => (n.reasons.includes('ending') ? 'renewal' : 'payment');

/** One step of "Remind all": which person of how many, and the way to the next. */
interface Run {
  type: Type;
  step: number;
  total: number;
  sent: number;
  /** The next people in the run, for the Next list; none on the last step. */
  upcoming: NeedsYouDto[];
  onNext: (sent: boolean) => void;
}

/** Keyed by plan: another customer's screen never inherits this one's edited text. */
export function Remind() {
  const { subId = '' } = useParams();
  return <RemindFor key={subId} subId={subId} />;
}

const reason = (n: NeedsYouDto) => [n.reasons.includes('ending') ? endLabel(n.daysToEnd, n.endDate) : null, n.due > 0 ? `${formatInr(n.due)} due` : null].filter(Boolean).join(' · ');

/**
 * "Remind all" (P35): everyone on Today's Needs you, one after another. The list is the one
 * on screen when the run starts, so it doesn't reshuffle as payments come in.
 */
export function RemindAll() {
  const today = useToday();
  const [list, setList] = useState<NeedsYouDto[] | null>(null);
  const [step, setStep] = useState(0);
  const [sent, setSent] = useState(0);

  useEffect(() => { if (!list && today.data) setList(today.data.needsYou); }, [list, today.data]);

  const head = <Top><Back to="/" label="Today" /></Top>;
  if (!list && today.isError) return <Screen>{head}<LoadError what="today’s list" onRetry={() => void today.refetch()} /></Screen>;
  if (!list) return <Screen>{head}<PageLoader /></Screen>;

  if (step >= list.length) {
    return (
      <Screen gap={18}>
        {head}
        <Title h={list.length ? 'Reminders done' : 'No one to remind'} />
        <PCard>
          <PEmpty title={list.length ? `Sent ${sent} of ${list.length}` : 'All clear'}>
            {list.length ? (sent < list.length ? `${list.length - sent} skipped. They stay on Today until their plan or payment changes.` : 'Everyone on Needs you has their message.') : 'No plans ending soon and nothing to collect.'}
          </PEmpty>
        </PCard>
        <Btn label="Back to Today" go="check" to="/" />
      </Screen>
    );
  }

  const n = list[step]!;
  return (
    <RemindFor
      key={n.subscriptionId}
      subId={n.subscriptionId}
      run={{
        type: typeFor(n),
        step: step + 1,
        total: list.length,
        sent,
        upcoming: list.slice(step + 1, step + 3),
        onNext: (didSend) => { if (didSend) setSent((c) => c + 1); setStep((i) => i + 1); },
      }}
    />
  );
}

function RemindFor({ subId, run }: { subId: string; run?: Run }) {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [runType, setRunType] = useState<Type>(run?.type ?? 'renewal');
  const type = run ? runType : typeFrom(params.get('type'));
  const pickType = (t: Type) => (run ? setRunType(t) : setParams({ type: t }, { replace: true }));
  // In a run, opening WhatsApp turns Skip into "Next".
  const [opened, setOpened] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);

  const briefQ = useBrief(subId);
  const brief = briefQ.data;
  const msg = useMessage(subId, type);
  const today = useToday().data;

  // The other message types are written ahead, so switching type shows its text at once.
  useEffect(() => {
    for (const t of TYPES) void qc.prefetchQuery({ queryKey: ['message', subId, t.id], queryFn: () => get<MessageDto>(`/subscriptions/${subId}/message?type=${t.id}`) });
  }, [qc, subId]);
  // A new message type (picked here, or by going back) starts from its generated text again.
  useEffect(() => { setDraft(null); setEditing(false); }, [type]);

  const text = draft ?? msg.data?.text ?? '';
  const phone = msg.data?.phone || brief?.phone || '';
  const link = phone && text ? waLink(phone, text) : '';
  // The previous type's text, kept on screen while the new one is written: shown, but not sendable.
  const stale = msg.isPlaceholderData;
  const writing = msg.isPending || stale;
  const others = (today?.needsYou ?? []).filter((n) => n.subscriptionId !== subId).slice(0, 3);

  const head = (
    <>
      <Top><Back to="/" label="Today" /></Top>
      {run
        ? <Title h="Remind all" sub="One message at a time. WhatsApp opens with it ready, then you come back for the next." />
        : <Title h="Send a reminder" sub="WhatsApp opens with the message ready." />}
    </>
  );
  if (briefQ.isError && !brief) return <Screen gap={16}>{head}<LoadError what="this customer" onRetry={() => void briefQ.refetch()} /></Screen>;
  // The first message comes with the page, so its text never pushes the buttons down after they are drawn.
  if (!brief || (msg.isPending && !msg.isError)) return <Screen gap={16}>{head}<PageLoader /></Screen>;

  const ending = brief.state === 'active' && brief.daysToEnd >= 0 && brief.daysToEnd <= 3;
  const first = brief.name.split(' ')[0];
  return (
    <Screen gap={16}>
      {head}
      {run && (
        <PCard pad style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="p-sec"><Kick>Sent</Kick><b style={{ fontSize: 15 }}>{run.sent} of {run.total}</b></div>
          <Bar frac={run.sent / run.total} />
        </PCard>
      )}
      <PCard>
        <Row to={`/s/${brief.subscriberId}`} left={<Av name={brief.name} />} t={brief.name} s={[mealsLabel(brief.slot), brief.area].filter(Boolean).join(' · ')}
          right={ending ? <PBadge tone="r">{endLabel(brief.daysToEnd, brief.endDate)}</PBadge> : brief.due > 0 ? <PBadge tone="s">{formatInr(brief.due)} due</PBadge> : undefined} />
      </PCard>

      <PChips label="Message type">
        {TYPES.map((t) => <PChip key={t.id} on={type === t.id} onClick={() => pickType(t.id)}>{t.label}</PChip>)}
      </PChips>

      <div className="p-sec">
        <Kick>Message</Kick>
        <span className="p-pair">
          {writing && <span className="busy-mark" role="status" aria-label="Writing the message"><TiffinMini /></span>}
          <button type="button" className="p-link" disabled={writing || !text} onClick={() => { setDraft(text); setEditing((e) => !e); }}>{editing ? 'Done' : 'Edit'}</button>
        </span>
      </div>
      {msg.isPending ? <div className="p-msg" aria-hidden="true"><Skel w={240} /><Skel w={200} /><Skel w={260} /><Skel w={120} /></div>
        : editing ? <textarea className="plain p-msg-edit" aria-label="Message text" value={text} onChange={(e) => setDraft(e.target.value)} rows={8} />
        : <div className={`p-msg${stale ? ' stale' : ''}`}><WhatsAppText text={text} /></div>}

      {run ? (
        <G2>
          <Btn2 label={opened ? (run.upcoming[0] ? `Next: ${run.upcoming[0].name.split(' ')[0]}` : 'Finish') : `Skip ${first}`} onClick={() => run.onNext(opened)} />
          <Btn label={opened ? 'Again' : 'Send'} go="send" href={link} disabled={!link || writing} onClick={() => setOpened(true)} />
        </G2>
      ) : (
        <Btn label="Open in WhatsApp" go="chat" href={link} disabled={!link || writing} />
      )}

      {run ? run.upcoming.length > 0 && (
        <Group label="Next">
          <PCard>{run.upcoming.map((n) => <Row key={n.subscriptionId} left={<Av name={n.name} />} t={n.name} s={reason(n)} />)}</PCard>
        </Group>
      ) : others.length > 0 && (
        <Group label="Next to remind" right={<Kick>{others.length} more</Kick>}>
          <PCard>
            {others.map((n) => (
              <Row key={n.subscriptionId} left={<Av name={n.name} />} t={<Link to={`/s/${n.subscriberId}`} style={{ color: 'inherit', textDecoration: 'none' }}>{n.name}</Link>} s={reason(n)}
                right={<Sm label="Remind" onClick={() => nav(`/remind/${n.subscriptionId}?type=${n.reasons.includes('ending') ? 'renewal' : 'payment'}`)} ariaLabel={`Remind ${n.name.split(' ')[0]} on WhatsApp`} />} />
            ))}
          </PCard>
        </Group>
      )}
      <Note icon="send">You tap Send in WhatsApp. Nothing goes out by itself.</Note>
    </Screen>
  );
}
